// ============================================================================
// FSA POLICY NOTE — Section list + TipTap editor pane
// ============================================================================
//
// Used by the Note Modal when hydrate.formatType === 'POLICY' (Accounting
// Policies and Corporate Information).
//
// Layout — two-pane split:
//   · Left rail  — ordered section list (template sections merged with
//                  auditor-added). Each row shows section title + edited
//                  indicator + per-section actions (rename / hide / restore
//                  default / move up / down / delete). Selecting one loads
//                  it in the editor.
//   · Right pane — TipTap editor for the selected section. Auditor edits
//                  in WYSIWYG; changes pushed into
//                  workspace.policy_section_data[<code>].content.
//
// Section management (Slice 4):
//   · Add custom section (button at bottom of rail)
//   · Remove custom section (trash icon — custom sections only)
//   · Rename section (pencil icon — sets custom_title on template,
//     title on added)
//   · Hide / show section (eye icon — template sections only; toggling
//     on a custom section is equivalent to removing it)
//   · Restore default (revert icon — clears the workspace content
//     override so the template default re-applies)
//   · Reorder (up / down arrows — persists via policy_section_order)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import TiptapEditor from './TiptapEditor';
import type {
  NoteHydrate,
  NoteWorkspaceData,
  PolicySectionEntry,
  NoteAddedPolicySection,
} from 'api/types/fsa/noteData.types';

interface Props {
  hydrate: NoteHydrate;
  workspace: NoteWorkspaceData;
  setWorkspace: (next: NoteWorkspaceData) => void;
  readOnly?: boolean;
}

interface SectionView {
  /** Section code (template) or local_id (added). */
  code: string;
  title: string;
  /** TRUE when the auditor has edited the body content. */
  isEdited: boolean;
  /** TRUE when the auditor flagged the section as hidden. */
  isHidden: boolean;
  /** TRUE for auditor-added sections (not in template). */
  isCustom: boolean;
  /** Template default content (null for auditor-added sections). */
  defaultContent: unknown | null;
}

const PolicyEditorPane: React.FC<Props> = ({ hydrate, workspace, setWorkspace, readOnly = false }) => {
  // Parse template section definitions from columnSpecJson.
  const templateSections = useMemo(
    () => parseTemplateSections(hydrate.columnSpecJson),
    [hydrate.columnSpecJson],
  );

  // Merge template + added + apply order. Hidden sections still appear
  // in the rail (auditor can un-hide) but render greyed.
  const sections = useMemo(() => {
    const out: SectionView[] = [];
    const overrides = workspace.policy_section_data ?? {};

    for (const t of templateSections) {
      const ov = overrides[t.code];
      out.push({
        code: t.code,
        title: ov?.custom_title ?? t.title,
        isEdited: ov?.content != null,
        isHidden: ov?.is_hidden === true,
        isCustom: false,
        defaultContent: t.default_content,
      });
    }
    for (const a of workspace.added_policy_sections ?? []) {
      const ov = overrides[a.local_id];
      out.push({
        code: a.local_id,
        title: ov?.custom_title ?? a.title,
        isEdited: true, // added sections are inherently edited
        isHidden: ov?.is_hidden === true,
        isCustom: true,
        defaultContent: a.content ?? null,
      });
    }

    // Apply explicit order if persisted; else template index, then
    // added sections in registration order.
    const order = workspace.policy_section_order;
    if (order && order.length > 0) {
      const indexOf = new Map<string, number>();
      order.forEach((c, i) => { if (!indexOf.has(c)) indexOf.set(c, i); });
      out.sort((a, b) => {
        const ai = indexOf.has(a.code) ? indexOf.get(a.code)! : Number.MAX_SAFE_INTEGER;
        const bi = indexOf.has(b.code) ? indexOf.get(b.code)! : Number.MAX_SAFE_INTEGER;
        return ai - bi;
      });
    }
    return out;
  }, [templateSections, workspace.policy_section_data, workspace.added_policy_sections, workspace.policy_section_order]);

  const [selectedCode, setSelectedCode] = useState<string | null>(sections[0]?.code ?? null);

  // Keep selection valid as the list changes.
  useEffect(() => {
    if (sections.length === 0) {
      setSelectedCode(null);
      return;
    }
    if (selectedCode == null || !sections.some(s => s.code === selectedCode)) {
      setSelectedCode(sections[0].code);
    }
  }, [sections, selectedCode]);

  const selected = sections.find(s => s.code === selectedCode) ?? null;

  // Effective content for selected — workspace override OR template default.
  const effectiveContent = useMemo(() => {
    if (!selected) return undefined;
    const ov = workspace.policy_section_data?.[selected.code];
    if (ov?.content != null) return ov.content;
    return selected.defaultContent ?? undefined;
  }, [selected, workspace.policy_section_data]);

  // ---- Mutators ----------------------------------------------------------

  const mutateEntry = useCallback((code: string, patch: Partial<PolicySectionEntry> | null) => {
    const prev = workspace.policy_section_data ?? {};
    const prevEntry = prev[code] ?? {};
    let nextEntry: PolicySectionEntry | undefined;
    if (patch === null) {
      nextEntry = undefined;
    } else {
      nextEntry = { ...prevEntry, ...patch };
      // If every field is empty / undefined, drop the entry entirely
      // (keep the JSONB small).
      if (
        nextEntry.content == null
        && nextEntry.is_hidden == null
        && (nextEntry.custom_title == null || nextEntry.custom_title === '')
      ) {
        nextEntry = undefined;
      }
    }
    const nextDict = { ...prev };
    if (nextEntry == null) delete nextDict[code];
    else nextDict[code] = nextEntry;
    setWorkspace({ ...workspace, policy_section_data: nextDict });
  }, [workspace, setWorkspace]);

  const handleEditorChange = useCallback((doc: unknown) => {
    if (!selected) return;
    mutateEntry(selected.code, { content: doc });
  }, [selected, mutateEntry]);

  const handleToggleHidden = useCallback((code: string) => {
    const cur = workspace.policy_section_data?.[code]?.is_hidden === true;
    mutateEntry(code, { is_hidden: cur ? undefined : true });
  }, [workspace.policy_section_data, mutateEntry]);

  const handleRename = useCallback((code: string, currentTitle: string) => {
    // eslint-disable-next-line no-alert
    const next = window.prompt('Section title:', currentTitle);
    if (next == null) return;
    const trimmed = next.trim();
    if (trimmed === '' || trimmed === currentTitle) {
      // Reset to template/added default when blanked.
      mutateEntry(code, { custom_title: undefined });
      return;
    }
    // For auditor-added sections, edit the entry's title directly so
    // the rename survives without a separate override mechanism.
    const added = workspace.added_policy_sections ?? [];
    const addedIdx = added.findIndex(a => a.local_id === code);
    if (addedIdx >= 0) {
      const nextAdded = added.slice();
      nextAdded[addedIdx] = { ...nextAdded[addedIdx], title: trimmed };
      setWorkspace({ ...workspace, added_policy_sections: nextAdded });
      return;
    }
    // Template section → store rename in policy_section_data.custom_title.
    mutateEntry(code, { custom_title: trimmed });
  }, [workspace, setWorkspace, mutateEntry]);

  const handleRestoreDefault = useCallback((code: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Restore the template default content for this section?')) return;
    mutateEntry(code, { content: undefined });
  }, [mutateEntry]);

  const handleMoveUp = useCallback((code: string) => {
    const codes = sections.map(s => s.code);
    const idx = codes.indexOf(code);
    if (idx <= 0) return;
    const next = codes.slice();
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setWorkspace({ ...workspace, policy_section_order: next });
  }, [sections, workspace, setWorkspace]);

  const handleMoveDown = useCallback((code: string) => {
    const codes = sections.map(s => s.code);
    const idx = codes.indexOf(code);
    if (idx < 0 || idx >= codes.length - 1) return;
    const next = codes.slice();
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setWorkspace({ ...workspace, policy_section_order: next });
  }, [sections, workspace, setWorkspace]);

  const handleAdd = useCallback(() => {
    // eslint-disable-next-line no-alert
    const title = window.prompt('New section title:');
    if (title == null) return;
    const trimmed = title.trim();
    if (trimmed === '') return;

    const localId = generateLocalId();
    const newSection: NoteAddedPolicySection = {
      local_id: localId,
      title: trimmed,
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      display_order: 1000 + (workspace.added_policy_sections?.length ?? 0) * 10,
    };
    const codes = sections.map(s => s.code);
    const nextOrder = codes.length > 0 ? [...codes, localId] : undefined;
    setWorkspace({
      ...workspace,
      added_policy_sections: [...(workspace.added_policy_sections ?? []), newSection],
      policy_section_order: nextOrder,
    });
    setSelectedCode(localId);
  }, [sections, workspace, setWorkspace]);

  const handleRemove = useCallback((code: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this custom section? Its content will be discarded.')) return;
    const nextAdded = (workspace.added_policy_sections ?? []).filter(a => a.local_id !== code);
    const prevData = workspace.policy_section_data ?? {};
    const nextData = { ...prevData };
    delete nextData[code];
    const nextOrder = workspace.policy_section_order?.filter(c => c !== code);
    setWorkspace({
      ...workspace,
      added_policy_sections: nextAdded,
      policy_section_data: nextData,
      policy_section_order: nextOrder,
    });
  }, [workspace, setWorkspace]);

  // ---- Render ------------------------------------------------------------

  if (sections.length === 0) {
    return (
      <div style={emptyStateStyle}>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          No policy sections defined.
        </div>
        {!readOnly && (
          <button type="button" className="btn btn-primary btn-sm" onClick={handleAdd}
            style={{ marginTop: '0.75rem' }}>
            <IconSystem.add size={14} /> Add section
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={paneStyle}>
      {/* Section list (left rail) */}
      <aside style={sectionListStyle}>
        <header style={sectionListHeaderStyle}>
          <span>Policy sections</span>
          <span style={countStyle}>{sections.length}</span>
        </header>
        <div style={sectionListBodyStyle}>
          {sections.map((s, idx) => {
            const isSelected = s.code === selectedCode;
            return (
              <div
                key={s.code}
                style={{
                  ...sectionRowStyle,
                  background: isSelected ? 'var(--bg-secondary)' : 'transparent',
                  borderLeftColor: isSelected ? '#2563eb' : 'transparent',
                  opacity: s.isHidden ? 0.55 : 1,
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedCode(s.code)}
                  style={sectionRowMainStyle}
                  title={s.title}
                >
                  <span style={sectionIndexStyle}>{idx + 1}.</span>
                  <span style={{
                    ...sectionTitleStyle,
                    fontWeight: isSelected ? 600 : 400,
                    fontStyle: s.isHidden ? 'italic' : 'normal',
                  }}>
                    {s.title}
                  </span>
                  {s.isEdited && !s.isCustom && <span style={editedDotStyle} title="Edited" />}
                  {s.isCustom && <span style={customChipStyle}>custom</span>}
                </button>

                {!readOnly && (
                  <div style={sectionActionsStyle}>
                    <RowAction
                      onClick={() => handleMoveUp(s.code)}
                      disabled={idx === 0}
                      title="Move up"
                    >
                      <IconSystem.chevronUp size={12} />
                    </RowAction>
                    <RowAction
                      onClick={() => handleMoveDown(s.code)}
                      disabled={idx === sections.length - 1}
                      title="Move down"
                    >
                      <IconSystem.chevronDown size={12} />
                    </RowAction>
                    <RowAction
                      onClick={() => handleRename(s.code, s.title)}
                      title="Rename section"
                    >
                      <IconSystem.edit size={12} />
                    </RowAction>
                    {!s.isCustom && (
                      <RowAction
                        onClick={() => handleToggleHidden(s.code)}
                        title={s.isHidden ? 'Show in print' : 'Hide from print'}
                      >
                        {s.isHidden
                          ? <IconSystem.eye size={12} />
                          : <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>👁</span>}
                      </RowAction>
                    )}
                    {!s.isCustom && s.isEdited && (
                      <RowAction
                        onClick={() => handleRestoreDefault(s.code)}
                        title="Restore template default"
                      >
                        <IconSystem.refresh size={12} />
                      </RowAction>
                    )}
                    {s.isCustom && (
                      <RowAction
                        onClick={() => handleRemove(s.code)}
                        title="Remove custom section"
                        danger
                      >
                        <IconSystem.trash size={12} />
                      </RowAction>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && (
          <footer style={sectionListFooterStyle}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleAdd}
              style={{ width: '100%' }}
            >
              <IconSystem.add size={13} /> Add section
            </button>
          </footer>
        )}
      </aside>

      {/* Editor (right pane) */}
      <main style={editorPaneStyle}>
        {selected ? (
          <>
            <header style={editorHeaderStyle}>
              <h3 style={editorTitleStyle}>{selected.title}</h3>
              {selected.isCustom && <span style={customChipStyle}>custom</span>}
              {selected.isHidden && (
                <span style={hiddenChipStyle}>hidden — won't print</span>
              )}
            </header>
            <div style={editorContainerStyle}>
              <TiptapEditor
                sectionKey={selected.code}
                initialContent={effectiveContent}
                onChange={handleEditorChange}
                readOnly={readOnly || selected.isHidden}
              />
            </div>
          </>
        ) : (
          <div style={emptyStateStyle}>Select a section to edit.</div>
        )}
      </main>
    </div>
  );
};

// ============================================================================
// RowAction button
// ============================================================================

interface RowActionProps {
  onClick: () => void;
  title: string;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}

const RowAction: React.FC<RowActionProps> = ({ onClick, title, disabled, danger, children }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    disabled={disabled}
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22,
      border: 'none', borderRadius: 3,
      background: 'transparent',
      color: disabled ? 'var(--text-muted, #94a3b8)' : danger ? '#dc2626' : 'var(--text-secondary)',
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    {children}
  </button>
);

// ============================================================================
// Helpers
// ============================================================================

interface TemplateSectionDef {
  code: string;
  title: string;
  default_content: unknown;
}

function parseTemplateSections(columnSpecJson: string | null | undefined): TemplateSectionDef[] {
  if (!columnSpecJson) return [];
  try {
    const arr = JSON.parse(columnSpecJson);
    if (!Array.isArray(arr)) return [];
    return arr
      .filter((s: any) => s && typeof s === 'object' && typeof s.code === 'string')
      .map((s: any) => ({
        code: s.code,
        title: typeof s.title === 'string' ? s.title : s.code,
        default_content: s.default_content ?? null,
      }));
  } catch {
    return [];
  }
}

function generateLocalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return 'sec-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

// ============================================================================
// Styles
// ============================================================================

const paneStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
};
const sectionListStyle: React.CSSProperties = {
  width: 280,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const sectionListHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.75rem', fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: 0.4,
};
const sectionListBodyStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '0.25rem 0',
};
const sectionListFooterStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderTop: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
};
const sectionRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  borderLeft: '3px solid transparent',
};
const sectionRowMainStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '0.45rem',
  padding: '0.4rem 0.5rem 0.4rem 0.75rem',
  background: 'transparent',
  border: 'none',
  textAlign: 'left',
  fontSize: '0.8125rem',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  minWidth: 0,
};
const sectionActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  paddingRight: '0.25rem',
};
const sectionIndexStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.72rem',
  color: 'var(--text-secondary)',
  minWidth: 18,
  textAlign: 'right',
};
const sectionTitleStyle: React.CSSProperties = {
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const editedDotStyle: React.CSSProperties = {
  width: 6, height: 6,
  borderRadius: '50%',
  background: '#2563eb',
  flexShrink: 0,
};
const countStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontWeight: 500,
};
const customChipStyle: React.CSSProperties = {
  padding: '1px 6px',
  borderRadius: 999,
  background: '#f3e8ff',
  color: '#6b21a8',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const hiddenChipStyle: React.CSSProperties = {
  padding: '1px 6px',
  borderRadius: 999,
  background: '#f1f5f9',
  color: '#64748b',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};
const editorPaneStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-secondary)',
};
const editorHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.5rem 0.875rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-primary)',
};
const editorTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const editorContainerStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: '0.625rem 0.875rem',
};
const emptyStateStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
};

export default PolicyEditorPane;
